import { Platform } from "react-native";

import type { PartialTheme } from "$src/core/models/theme";
import opacity from "$src/core/utils/color/opacity";
import CustomPlatform from "$src/core/utils/platform/platform";
import icons from "$src/flavors/hyview/assets/icons/generated/icons.glyphmap.json";

type FontFamily = "Rubik" | "Arial" | "Roboto";

export const FONT_RESOURCES = {
  /* eslint-disable @typescript-eslint/no-require-imports */
  "Rubik-Regular": require("../assets/fonts/Rubik-Regular.ttf"),
  "Rubik-Medium": require("../assets/fonts/Rubik-Medium.ttf"),
  ...(Platform.OS === "web"
    ? {
        [icons.m.f]: {
          uri: require("../assets/icons/generated/icons.ttf"),
          // Icon fonts have no BESbswy glyphs; FontFaceObserver needs a PUA char.
          testString: String.fromCodePoint(icons.m.s),
        },
      }
    : {}),
  /* eslint-enable @typescript-eslint/no-require-imports */
} as const satisfies Record<
  string,
  number | { uri: number; testString: string }
>;

const FONTS = {
  Rubik: {
    normal: {
      400: "Rubik-Regular",
      500: "Rubik-Medium",
    },
  },
  // System faces, so neither is bundled: Arial ships on Apple platforms and
  // the web, Roboto on Android, where Arial does not exist at all.
  Arial: {
    normal: {
      400: "Arial",
    },
  },
  Roboto: {
    normal: {
      400: "Roboto",
    },
  },
} as const satisfies Record<FontFamily, Record<string, Record<string, string>>>;

// Named per target rather than behind a default, so each platform's face is
// stated: Arial exists on Apple platforms and the web, Roboto on Android.
const SANS = CustomPlatform.select<string>({
  ios: FONTS.Arial.normal["400"],
  tvOS: FONTS.Arial.normal["400"],
  web: FONTS.Arial.normal["400"],
  android: FONTS.Roboto.normal["400"],
  androidTV: FONTS.Roboto.normal["400"],
});

const COLORS = {
  black: "#000000",
  white: "#FFFFFF",
  gray100: "#f5f5f5",
  gray200: "#e5e5e5",
  gray300: "#d4d4d4",
  gray400: "#a3a3a3",
  gray500: "#737373",
  gray600: "#525252",
  gray700: "#404040",
  gray800: "#262626",
  gray900: "#171717",
  negative: "#ef4444",
  positive: "#22c55e",
  brand: "#ffc400",
} as const satisfies Record<string, string>;

const THEME: PartialTheme = {
  font: {
    families: {
      heading1: SANS,
      heading2: SANS,
      heading3: SANS,
      heading4: SANS,
      heading5: SANS,
      heading6: SANS,
      heading7: SANS,
      body: SANS,
      button: FONTS.Rubik.normal["500"],
      sub: FONTS.Rubik.normal["400"],
    },
  },
  colors: {
    backgroundDefault: COLORS.gray900,
    backgroundSurface: COLORS.gray700,
    backgroundFloat: COLORS.gray600,
    backgroundActive: COLORS.gray500,
    backgroundFocus: COLORS.white,
    backgroundHover: opacity(COLORS.gray800, 0.8),
    backgroundInverted: COLORS.white,
    backgroundPositive: COLORS.positive,
    backgroundNegative: COLORS.negative,
    backgroundBrand: COLORS.brand,
    backgroundBackdrop: opacity(COLORS.black, 0.9),

    foregroundDefault: COLORS.white,
    foregroundDefaultMuted: opacity(COLORS.white, 0.5),
    foregroundBrand: COLORS.brand,
    foregroundOnSurface: COLORS.white,
    foregroundOnFloat: COLORS.white,
    foregroundOnActive: COLORS.white,
    foregroundOnFocus: COLORS.white,
    foregroundOnHover: COLORS.white,
    foregroundOnInverted: COLORS.gray900,
    foregroundOnPositive: COLORS.white,
    foregroundOnNegative: COLORS.white,
    foregroundOnBrand: COLORS.white,

    borderDefault: opacity(COLORS.gray900, 0.1),
    borderFocus: COLORS.white,
    borderPositive: COLORS.positive,
    borderNegative: COLORS.negative,
    borderBrand: COLORS.brand,
  },
};

export default THEME;
